import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Gift, Users, Timer, CheckCircle2, TrendingUp, Hourglass, Wallet, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ReferralShareCard from "../components/ReferralShareCard";
import GooglePlayBadge from "../components/GooglePlayBadge";

const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

function StatCard({ icon: Icon, label, value, hint, tone = "emerald", testid }) {
  const toneMap = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    sky: "text-sky-700 bg-sky-50 border-sky-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
  };
  return (
    <div className={`rounded-md border p-4 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-1 font-display text-2xl font-bold tabular">{value}</div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

function WithdrawDialog({ open, onOpenChange, balance, minWithdrawal, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setAmount(""); setNote(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Amount daalein");
    if (amt < minWithdrawal) return toast.error(`Minimum PKR ${minWithdrawal.toLocaleString()}`);
    if (amt > balance) return toast.error("Wallet mein itni balance nahi hai");
    setBusy(true);
    try {
      await api.post("/refer/withdrawals", { amount: amt, note });
      toast.success("Withdrawal request submit ho gayi — admin approval ke baad transfer ho jayegi.");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Withdrawal</DialogTitle>
          <DialogDescription>
            Available: <b>{fmtPKR(balance)}</b> · Minimum: PKR {minWithdrawal.toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Amount (PKR)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={minWithdrawal}
              max={balance}
              required
              data-testid="refer-withdraw-amount"
            />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Bank details ya note"
              data-testid="refer-withdraw-note"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="refer-withdraw-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Refer() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [trialCustomers, setTrialCustomers] = useState({ buckets: {}, shops: [] });
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dash, wds, trial] = await Promise.all([
        api.get("/refer/dashboard").then((r) => r.data),
        api.get("/refer/withdrawals").then((r) => r.data.items).catch(() => []),
        api.get("/refer/trial-customers").then((r) => r.data).catch(() => ({ buckets: {}, shops: [] })),
      ]);
      setData(dash);
      setHistory(wds);
      setTrialCustomers(trial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="refer-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2.5">
            <Gift className="w-7 h-7 text-emerald-600" /> Refer &amp; Earn
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Har naya shop jo aap ke referral se signup kare aur <b className="text-emerald-700">PKR {(data.plan_price || 1500).toLocaleString()}</b> subscription pay kare, aap ko <b className="text-emerald-700">PKR {(data.referral_reward_fixed || 500).toLocaleString()} fixed reward</b> milega — har qualifying payment par.
          </p>
        </div>
      </div>

      {/* Share card — link, code, WA, QR, copy */}
      <ReferralShareCard
        referralCode={data.referral_code}
        referralLink={data.referral_link}
        planPrice={data.plan_price || 1500}
        referralRewardFixed={data.referral_reward_fixed || 500}
        stats={{
          link_clicks: data.link_clicks || 0,
          signups: data.signups || 0,
          trial_accounts: data.trial_accounts || 0,
          paid_shops: data.paid_shops || 0,
          conversion_rate: data.conversion_rate || 0,
        }}
      />

      {/* KPIs — first vs renewal split */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard icon={Users}         label="Total Referrals"        value={data.signups || 0}          hint="All time signups"                 tone="slate"   testid="refer-card-signups" />
        <StatCard icon={Timer}         label="Signups Pending Payment" value={data.trial_accounts || 0}   hint="Yet to pay PKR 1,500" tone="amber" testid="refer-card-trial" />
        <StatCard icon={CheckCircle2}  label="Active Paid Referrals"  value={data.active_paid_referrals ?? data.paid_shops ?? 0} hint="Currently paying"       tone="emerald" testid="refer-card-paid" />
        <StatCard icon={TrendingUp}    label="Total Earnings"         value={fmtPKR(data.commission_total)} hint="Lifetime paid"                 tone="emerald" testid="refer-card-earnings" />
        <StatCard icon={Sparkles}      label={`Reward per Paid Referral`} value={fmtPKR(data.referral_reward_fixed || 500)} hint="Fixed on every PKR 1,500 sub" tone="emerald" testid="refer-card-first" />
        <StatCard icon={TrendingUp}    label="Approved (Awaiting Payout)" value={fmtPKR(data.commission_approved || 0)}      hint="Ready to withdraw" tone="sky"   testid="refer-card-renewal" />
        <StatCard icon={Hourglass}     label="Pending Commission"     value={fmtPKR(data.commission_pending)} hint="Admin approval"           tone="amber"   testid="refer-card-pending" />
        <StatCard icon={Wallet}        label="Total Withdrawn"        value={fmtPKR(data.total_withdrawn || 0)} hint={`${fmtPKR(data.pending_withdrawals || 0)} pending`} tone="slate" testid="refer-card-withdrawn" />
      </div>

      {/* Wallet + Withdraw */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-lg">Referral Wallet</div>
              <div className="text-xs text-slate-500">Minimum withdrawal: PKR {(data.min_withdrawal || 1000).toLocaleString()}</div>
            </div>
            <Button
              onClick={() => setWithdrawOpen(true)}
              disabled={data.wallet_balance < data.min_withdrawal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="refer-withdraw-btn"
            >
              <Wallet className="w-4 h-4 mr-1.5" /> Request Withdrawal
            </Button>
          </div>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold tabular text-emerald-700" data-testid="refer-wallet-balance">
              {fmtPKR(data.wallet_balance)}
            </span>
            <span className="text-sm text-slate-500">balance</span>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <div className="font-display font-semibold text-lg">How it works</div>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>1. Apna referral link ya code customer/dost ko WhatsApp par bhejein.</li>
            <li>2. Woh HisaabPro website ya Android app par signup karta hai.</li>
            <li>3. WhatsApp verification ke baad shop activate hoti hai — koi free trial nahi.</li>
            <li>4. Woh <b>PKR {(data.plan_price || 1500).toLocaleString()}</b> subscription pay karta hai.</li>
            <li>5. Aap ko <b className="text-emerald-700">PKR {(data.referral_reward_fixed || 500).toLocaleString()} fixed reward</b> milta hai — har qualifying paid subscription par.</li>
            <li>6. PKR {(data.min_withdrawal || 500).toLocaleString()}+ balance hone par kabhi bhi Withdraw karein.</li>
          </ul>
          <div className="mt-4 flex items-center gap-3">
            <GooglePlayBadge size="md" testId="refer-play-badge" />
            <div className="text-[11px] text-slate-500">Customer ko batayein — Play Store par bhi available hai.</div>
          </div>
        </div>
      </div>

      {/* My Trial Customers — Feb 2026 */}
      {trialCustomers && (trialCustomers.shops || []).length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="refer-trial-customers">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-display font-semibold text-lg flex items-center gap-2"><Timer className="w-5 h-5 text-amber-600" /> My Trial Customers</div>
              <div className="text-xs text-slate-500 mt-0.5">Aap ke referred shops jo trial pe hain — help them subscribe to unlock your commission.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {[
              { key: "trial_active",  label: "Trial Active",   tone: "amber",   test: "refer-trial-active" },
              { key: "expiring_soon", label: "Expiring Soon",  tone: "rose",    test: "refer-trial-expiring" },
              { key: "expired",       label: "Expired",        tone: "slate",   test: "refer-trial-expired" },
              { key: "converted",     label: "Converted",      tone: "emerald", test: "refer-trial-converted" },
            ].map((b) => (
              <div key={b.key} className={`rounded-md border p-2.5 ${
                b.tone === "amber" ? "border-amber-200 bg-amber-50" :
                b.tone === "rose" ? "border-rose-200 bg-rose-50" :
                b.tone === "emerald" ? "border-emerald-200 bg-emerald-50" :
                "border-slate-200 bg-slate-50"
              }`} data-testid={b.test}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{b.label}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">{trialCustomers.buckets?.[b.key] || 0}</div>
              </div>
            ))}
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {trialCustomers.shops.slice(0, 20).map((s) => (
              <div key={s.shop_id} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`refer-trial-row-${s.shop_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm">{s.shop_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.owner_name || "Owner"} {s.city ? `· ${s.city}` : ""}
                    {" · "}
                    <span className={s.converted ? "text-emerald-700 font-semibold" : s.days_delta < 0 ? "text-rose-700 font-semibold" : s.days_delta <= 2 ? "text-amber-700 font-semibold" : "text-slate-600"}>
                      {s.converted ? "Converted to paid" :
                       s.days_delta === null || s.days_delta === undefined ? "Unknown" :
                       s.days_delta < 0 ? `${-s.days_delta} day(s) expired` :
                       s.days_delta === 0 ? "Expires today" :
                       `${s.days_delta} day(s) remaining`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {s.owner_mobile && !s.converted ? (
                    <>
                      <a href={`tel:${s.owner_mobile}`} className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid={`refer-trial-call-${s.shop_id}`}>Call</a>
                      <a
                        href={`https://wa.me/${s.owner_mobile.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Assalam-o-Alaikum ${s.owner_name || ""}, HisaabPro trial expiry approach kar rahi hai. Quarterly plan se aap ka business unlocked rahe ga.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`refer-trial-wa-${s.shop_id}`}
                      >
                        WhatsApp
                      </a>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      const link = `${data.referral_link}`;
                      navigator.clipboard?.writeText(link);
                      toast.success("Subscription link copied");
                    }}
                    className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid={`refer-trial-copy-${s.shop_id}`}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Withdrawal history */}
      {history.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="refer-withdrawal-history">
          <div className="font-display font-semibold text-lg mb-3">Withdrawal History</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="py-2">Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((w) => (
                  <tr key={w.id} data-testid={`refer-wd-row-${w.id}`}>
                    <td className="py-2 text-slate-600">{(w.created_at || "").slice(0, 10)}</td>
                    <td className="font-semibold tabular">{fmtPKR(w.amount)}</td>
                    <td>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        w.status === "paid" ? "bg-emerald-100 text-emerald-700"
                        : w.status === "rejected" ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"
                      }`}>
                        {w.status}
                      </span>
                    </td>
                    <td className="text-slate-500 text-xs">{w.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <WithdrawDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        balance={data.wallet_balance}
        minWithdrawal={data.min_withdrawal || 1000}
        onDone={load}
      />
    </div>
  );
}
